import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuPanel, MatMenuTrigger } from '@angular/material/menu';
import { Subscription } from 'rxjs';
import { AppEvent } from 'src/app/data/models/app-event';
import { ChatMessageDto } from 'src/app/data/models/dto/chat-message.dto';
import { AppEventType } from 'src/app/data/models/enum/app-event-type.enum';
import { ChatTypeEnum } from 'src/app/data/models/enum/chat-type.enum';
import { SnackbarTypeEnum } from 'src/app/data/models/enum/snackbar-type.enum';
import { EventQueueService } from 'src/app/data/services/event-queue.service';
import { UserPreferencesService } from 'src/app/data/services/user-preferences.service';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { CustomSnackbarService } from 'src/app/ui/components/custom-snackbar/custom-snackbar-service';

@Component({
  selector: 'app-chat-item-menu',
  templateUrl: './chat-item-menu.component.html',
  styleUrls: ['./chat-item-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatItemMenuComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  contextMenuImage = 'expand_more';
  contextMenuVisibility = 'hidden';

  @Input() isVisible: boolean = false;

  @Input() openMenu?: boolean = false;
  @Output() openMenuChange = new EventEmitter<boolean>();

  @Input() chat!: ChatMessageDto;
  @Output() chatChange = new EventEmitter<ChatMessageDto>();

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  subscriptions: Subscription = new Subscription();

  constructor(
    private eventQueue: EventQueueService,
    private readonly userUtil: UserUtil,
    private readonly userPreferenceService: UserPreferencesService,
    private readonly snackbarService: CustomSnackbarService
  ) {}

  get idUser(): string {
    return this.userUtil.getUser().id;
  }

  get archived(): boolean {
    return this.chat.message.archived;
  }

  get unread(): boolean {
    return this.chat.message.unread;
  }

  ngAfterViewInit(): void {
    this.menuTrigger.menuClosed.subscribe(() =>
      this.openMenuChange.emit(false)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions.add(this.handleMenuArquiveEvents());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isVisible) {
      const newValue = changes.isVisible.currentValue;
      this.contextMenuVisibility = newValue ? 'visible' : 'hidden';
    }
    if (changes.openMenu?.currentValue) {
      this.menuTrigger?.openMenu();
      this.dispatchMenuEvent();
    }
  }

  setContextMenuVisibility() {
    if (this.menuTrigger) {
      if (this.menuTrigger.menuOpen) this.contextMenuVisibility = 'visible';
      else this.contextMenuVisibility = this.isVisible ? 'visible' : 'hidden';
    }
  }

  onClickOutside() {
    this.menuTrigger.closeMenu();
  }

  onMenuClick(event: MouseEvent) {
    event.stopPropagation();
    this.dispatchMenuEvent();
  }

  private dispatchMenuEvent() {
    if (this.menuTrigger) {
      this.eventQueue.dispatch(
        new AppEvent<MatMenuPanel>(
          AppEventType.ARQUIVE_ROOM_MENU_PANEL,
          this.menuTrigger.menu
        )
      );
    }
  }

  private handleMenuArquiveEvents() {
    return this.eventQueue
      .on<MatMenuPanel>(AppEventType.ARQUIVE_ROOM_MENU_PANEL)
      .subscribe((evt) => {
        if (evt.payload !== this.menuTrigger.menu) {
          this.menuTrigger.closeMenu();
        }
      });
  }

  async archive() {
    try {
      if (this.chat.type === ChatTypeEnum.ROOM_MESSAGE) {
        await this.userPreferenceService.archiveRoom(this.chat.message.id);
      }
      if (this.chat.type === ChatTypeEnum.USER_MESSAGE) {
        await this.userPreferenceService.archiveUserChat(
          this.chat.message.user.id
        );
      }
      this.chat.message.archived = true;
      this.chatChange.emit(this.chat);
      this.archiveSuccessMessage();
    } catch (error) {
      this.errorMessage();
    }
  }

  async unarchive() {
    try {
      if (this.chat.type === ChatTypeEnum.ROOM_MESSAGE) {
        await this.userPreferenceService.unarchiveRoom(this.chat.message.id);
      }
      if (this.chat.type === ChatTypeEnum.USER_MESSAGE) {
        await this.userPreferenceService.unarchiveUserChat(
          this.chat.message.user.id
        );
      }
      this.chat.message.archived = false;
      this.chatChange.emit(this.chat);
      this.unarchiveSuccessMessage();
    } catch (error) {
      this.errorMessage();
    }
  }

  async markAsUnread() {
    try {
      if (this.chat.type === ChatTypeEnum.ROOM_MESSAGE) {
        await this.userPreferenceService.markRoomAsUnread(this.chat.message.id);
      }
      if (this.chat.type === ChatTypeEnum.USER_MESSAGE) {
        await this.userPreferenceService.markUserChatAsUnread(
          this.chat.message.user.id
        );
      }
      this.chat.message.unread = true;
      this.chatChange.emit(this.chat);
    } catch (error) {
      console.error('Falha ao marcar como não lida');
    }
  }

  async markAsRead() {
    try {
      if (this.chat.type === ChatTypeEnum.ROOM_MESSAGE) {
        await this.userPreferenceService.markRoomAsRead(this.chat.message);
      }
      if (this.chat.type === ChatTypeEnum.USER_MESSAGE) {
        await this.userPreferenceService.markUserChatAsRead(this.chat.message);
      }
      this.chat.message.unread = false;
      this.chatChange.emit(this.chat);
    } catch (error) {
      console.error('Falha ao marcar como lida');
    }
  }

  private successMessage(successMessage: string) {
    this.snackbarService.open(
      successMessage,
      SnackbarTypeEnum.SUCCESS,
      'center',
      'bottom'
    );
  }

  private errorMessage() {
    this.snackbarService.open(
      'Não foi possível arquivar mensagem no momento',
      SnackbarTypeEnum.ERROR,
      'center',
      'bottom'
    );
  }

  private archiveSuccessMessage() {
    this.successMessage('Mensagem arquivada com sucesso');
  }

  private unarchiveSuccessMessage() {
    this.successMessage('Mensagem desarquivada com sucesso');
  }
}
